import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import cx from 'classnames';

import type {LockStyle} from '@pexip/components';
import {
    Box,
    Text,
    FontVariant,
    Button,
    IconTypes,
    ConfirmationModal,
    Divider,
    Accordion,
} from '@pexip/components';

import type {
    BreakoutRoomId,
    BreakoutRoomName,
    ParticipantID,
    InMeetingParticipant,
    RoomID,
} from '../../types';
import {BreakoutRoomVariant} from '../../types';
import {BreakoutRoomParticipantList} from '../BreakoutRoomParticipantList/BreakoutRoomParticipantList.view';
import {BreakoutRoomHeader} from '../BreakoutRoomHeader/BreakoutRoomHeader.view';
import {MainBreakoutRoomId} from '../../constants';
import {TestId} from '../../../test/testIds';
import {ResponsiveButton} from '../ResponsiveButton/ResponsiveButton.view';

import styles from './BreakoutRoom.module.scss';

export const BreakoutRoom = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<typeof Box> & {
        id: BreakoutRoomId;
        name: BreakoutRoomName;
        participants: InMeetingParticipant[];
        myIdentity?: string;
        amIControlOnlyParticipant?: boolean;
        rooms?: Record<BreakoutRoomId, BreakoutRoomName>;
        variant?: BreakoutRoomVariant;
        isDragTarget?: boolean;
        scrollElementRef?: React.RefObject<HTMLElement | null>;
        canEditRoomName?: boolean;
        renderParticipants?: (
            participants: InMeetingParticipant[],
        ) => React.ReactElement;
        onNameChanged?: (id: RoomID, name: string) => boolean | undefined;
        onParticipantDragging?: (participantDiv: HTMLDivElement) => void;
        onParticipantDrag?: (participantDiv: HTMLDivElement) => void;
        onParticipantRoomChange?: (
            targetParticipantUuid: ParticipantID,
            targetRoomId: RoomID,
        ) => void;
        _onAssignParticipants?: () => void;
        onJoinRoom?: (id: RoomID) => void;
        onCloseRoom?: (id: RoomID) => void;
        onRemoveRoom?: (id: RoomID) => void;
        lockStyle?: LockStyle;
    }
>(
    (
        {
            id,
            name,
            participants,
            myIdentity,
            amIControlOnlyParticipant,
            rooms,
            variant = BreakoutRoomVariant.Setup,
            onNameChanged,
            isDragTarget,
            scrollElementRef,
            canEditRoomName = false,
            renderParticipants,
            onParticipantDragging,
            onParticipantDrag,
            onParticipantRoomChange,
            _onAssignParticipants,
            onJoinRoom,
            onCloseRoom,
            onRemoveRoom,
            lockStyle,
            ...props
        },
        ref,
    ) => {
        const {t} = useTranslation();

        const [isCloseRoomModalOpen, setIsCloseRoomModalOpen] = useState(false);

        const isMainRoom = id === MainBreakoutRoomId;
        const iAmInThisRoom = participants.some(
            ({uuid}) => uuid === myIdentity,
        );

        const handleNameChange = useCallback(
            (name: string) => onNameChanged?.(id, name),
            [id, onNameChanged],
        );

        const closeRoom = useCallback(() => {
            setIsCloseRoomModalOpen(false);
            onCloseRoom?.(id);
        }, [id, onCloseRoom]);

        const FooterStartButton = () => {
            switch (variant) {
                case BreakoutRoomVariant.Setup:
                    return null;
                case BreakoutRoomVariant.Preview:
                    return !isMainRoom && onCloseRoom ? (
                        <Button
                            variant="dangerBordered"
                            onClick={() => setIsCloseRoomModalOpen(true)}
                            aria-label={t(
                                'breakouts.breakout-room.close-room-aria-label',
                                'Close breakout room',
                            )}
                            data-testid={TestId.CloseBreakoutRoom}
                        >
                            {t('common.close', 'Close')}
                        </Button>
                    ) : null;
                case BreakoutRoomVariant.Edit:
                    return null;
                // return !isMainRoom ? (
                //     <Button
                //         enhancerStart={
                //             <Icon
                //                 source={IconTypes.IconAddSomeone}
                //                 size="compact"
                //             />
                //         }
                //         onClick={onAssignParticipants}
                //         aria-label={t(
                //             'breakouts.breakout-room.assign-participants-aria-label',
                //             'Assign participants to breakout room {{name}}',
                //         )}
                //     >
                //         {t(
                //             'breakouts.breakout-room.assign-participants',
                //             'Assign Participants',
                //         )}
                //     </Button>
                // ) : null;
            }
        };

        const FooterEndButton = () => {
            switch (variant) {
                case BreakoutRoomVariant.Setup:
                case BreakoutRoomVariant.Edit:
                    return !isMainRoom ? (
                        <ResponsiveButton
                            variant="tertiary"
                            iconSource={IconTypes.IconTrash}
                            hideChildren={true}
                            onClick={() => onRemoveRoom?.(id)}
                            aria-label={t(
                                'breakouts.breakout-room.remove-room-aria-label',
                                'Remove breakout room',
                            )}
                            data-testid={TestId.RemoveBreakoutRoom}
                        />
                    ) : null;
                case BreakoutRoomVariant.Preview:
                    return !amIControlOnlyParticipant ? (
                        <Button
                            variant="success"
                            isDisabled={iAmInThisRoom}
                            aria-label={t(
                                'common.join-breakout-room-aria-label',
                                'Join breakout room',
                            )}
                            onClick={() => onJoinRoom?.(id)}
                            data-testid={TestId.JoinBreakoutRoom}
                        >
                            {t('common.join', 'Join')}
                        </Button>
                    ) : null;
            }
        };

        return (
            <Box
                {...props}
                data-testid={TestId.BreakoutRoom}
                id={id}
                ref={ref}
                className={cx(styles.wrapper, props.className, {
                    [styles.mainRoom]: id === MainBreakoutRoomId,
                    [styles.dragTarget]: isDragTarget,
                })}
            >
                <BreakoutRoomHeader
                    className="mb-2"
                    onSaveAction={handleNameChange}
                    name={
                        isMainRoom
                            ? t(
                                  'breakouts.breakout-room.main-name',
                                  'Main Room',
                              )
                            : name
                    }
                    showEditButton={canEditRoomName && !isMainRoom}
                />
                <Accordion
                    title={
                        <>
                            <Text
                                fontVariant={FontVariant.SmallBold}
                                className="me-2"
                            >
                                {id === MainBreakoutRoomId
                                    ? t(
                                          'breakouts.breakout-room.not-assigned',
                                          'Not assigned',
                                      )
                                    : t(
                                          'breakouts.breakout-room.assigned',
                                          'Assigned',
                                      )}
                            </Text>
                            <Text>{participants.length}</Text>
                        </>
                    }
                    aria-label={
                        id === MainBreakoutRoomId
                            ? t(
                                  'breakouts.breakout-room.not-assigned-aria-label',
                                  'Participants not assigned to a breakout room',
                              )
                            : t(
                                  'breakouts.breakout-room.assigned-aria-label',
                                  'Participants assigned to breakout room {{name}}',
                              )
                    }
                    isExpanded
                >
                    {renderParticipants ? (
                        renderParticipants(participants)
                    ) : (
                        <BreakoutRoomParticipantList
                            lockStyle={lockStyle}
                            scrollElementRef={scrollElementRef}
                            participants={participants}
                            myIdentity={myIdentity}
                            rooms={rooms}
                            onParticipantDragging={onParticipantDragging}
                            onParticipantDrag={onParticipantDrag}
                            onParticipantRoomChange={onParticipantRoomChange}
                            variant={variant}
                        />
                    )}
                    {isDragTarget && <Divider className={styles.dragDivider} />}
                </Accordion>
                <div className={styles.footer}>
                    <FooterStartButton />
                    <FooterEndButton />
                </div>
                <ConfirmationModal
                    cancelButtonText={t('common.cancel', 'Cancel')}
                    onCancel={() => setIsCloseRoomModalOpen(false)}
                    confirmButtonText={t('common.yes', 'Yes')}
                    onConfirm={closeRoom}
                    title={t('breakouts.close-room-modal.title', {
                        defaultValue: 'Close {{name}}?',
                        name,
                    })}
                    uniqueTitle={t('breakouts.close-room-modal.title', {
                        defaultValue: 'Close {{name}}?',
                        name,
                    })}
                    description={t(
                        'breakouts.close-room-modal.text',
                        'The participants in this room will be moved to the main room.',
                    )}
                    isOpen={isCloseRoomModalOpen}
                />
            </Box>
        );
    },
);
BreakoutRoom.displayName = 'BreakoutRoom';
